import type { ActivitySummary } from '../../data/dto';

/** The subset of an activity needed to reason about its recommended age band. */
type AgeBounded = Pick<ActivitySummary, 'age_from_weeks' | 'age_to_weeks'>;

/**
 * True when the baby's age (in whole weeks) falls within the activity's
 * recommended band. A missing lower bound means "from birth"; a missing
 * upper bound means "no upper limit".
 */
export function activityFitsAge(a: AgeBounded, ageWeeks: number): boolean {
  const from = a.age_from_weeks ?? 0;
  const to = a.age_to_weeks ?? Infinity;
  return ageWeeks >= from && ageWeeks <= to;
}

/**
 * Directional hint for an activity that sits outside the baby's current band:
 *  - 'younger' → aimed at younger babies (the baby has outgrown it: age_to < ageWeeks)
 *  - 'older'   → aimed at older babies (it's still early: age_from > ageWeeks)
 *  - null      → fits the baby's current age
 *
 * The two filters mirror the per-category list's "Younger babies" /
 * "Older babies" options, and together with `activityFitsAge` they partition
 * the catalog into exactly three buckets.
 */
export function activityAgeHint(a: AgeBounded, ageWeeks: number): 'younger' | 'older' | null {
  if (a.age_to_weeks != null && ageWeeks > a.age_to_weeks) return 'younger';
  if (a.age_from_weeks != null && ageWeeks < a.age_from_weeks) return 'older';
  return null;
}
